import React from 'react'
import { Formik, Form, Field, ErrorMessage } from 'formik'
import * as Yup from 'yup'
import { Input } from '../ui/Input'
import { ProposalQuoteAdjustments } from '@/types/management.types'

interface ProposalReviewFormProps {
  initialValues?: ProposalQuoteAdjustments
  onSubmit: (values: ProposalQuoteAdjustments) => void
  onChange?: (values: ProposalQuoteAdjustments) => void
}

const ProposalReviewForm: React.FC<ProposalReviewFormProps> = ({
  initialValues = {
    leaseTerm: 24,
    advancePayment: 90000,
    residualValue: 200000,
    guaranteeDeposit: 10000,
  },
  onSubmit,
  onChange,
}) => {
  const validationSchema = Yup.object({
    leaseTerm: Yup.number()
      .min(12, 'El plazo mínimo es de 12 meses')
      .max(48, 'El plazo máximo es de 48 meses')
      .required('El plazo de arrendamiento es requerido'),
    advancePayment: Yup.number()
      .min(0, 'La cantidad mínima es $0')
      .max(10000000, 'La cantidad máxima es $10,000,000')
      .required('El anticipo del arrendamiento es requerido'),
    residualValue: Yup.number()
      .min(0, 'La cantidad mínima es $0')
      .max(10000000, 'La cantidad máxima es $10,000,000')
      .required('El valor residual es requerido'),
    guaranteeDeposit: Yup.number()
      .min(0, 'La cantidad mínima es $0')
      .max(500000, 'La cantidad máxima es $500,000')
      .required('El depósito en garantía es requerido'),
  })

  const handleFieldChange = (
    fieldName: string,
    value: number,
    setFieldValue: any,
    values: ProposalQuoteAdjustments
  ) => {
    setFieldValue(fieldName, value)

    // Call onChange with updated values
    if (onChange) {
      const updatedValues = {
        ...values,
        [fieldName]: value,
      }
      onChange(updatedValues)
    }
  }

  return (
    <div className="max-w-lg mx-auto p-6 bg-white">
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
        enableReinitialize={true}
      >
        {({ values, setFieldValue }) => {
          return (
            <Form className="space-y-6">
              {/* Lease Term Slider */}
              <div>
                <h2 className="text-md font-bold mb-4 text-gray-800">
                  Selecciona el plazo de arrendamiento que prefieras:
                </h2>

                <div className="relative">
                  <Field name="leaseTerm">
                    {({ field }: any) => (
                      <div className="relative">
                        <input
                          {...field}
                          type="range"
                          min="12"
                          max="48"
                          step="1"
                          className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                          onChange={e => {
                            const newValue = parseInt(e.target.value)
                            handleFieldChange(
                              'leaseTerm',
                              newValue,
                              setFieldValue,
                              values
                            )
                          }}
                        />

                        {/* Slider labels */}
                        <div className="flex justify-between text-sm text-gray-600 mt-2">
                          <span>12 meses</span>
                          <span className="bg-green-500 text-white px-2 py-1 rounded text-xs">
                            {values.leaseTerm} meses
                          </span>
                          <span>48 meses</span>
                        </div>
                      </div>
                    )}
                  </Field>
                  <ErrorMessage
                    name="leaseTerm"
                    component="div"
                    className="text-red-500 text-sm mt-1"
                  />
                </div>
              </div>

              {/* Form Fields Section */}
              <div>
                <h2 className="text-md font-bold mb-4 text-gray-800">
                  Ajusta los valores clave de tu cotización:
                </h2>

                {/* Advance Payment */}
                <div className="mb-4">
                  <label
                    htmlFor="advancePayment"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Anticipo del arrendamiento*
                  </label>
                  <Field name="advancePayment">
                    {({ field }: any) => (
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">
                          $
                        </span>
                        <Input
                          {...field}
                          type="number"
                          min="0"
                          max="10000000"
                          className="pl-8 no-spinner"
                          placeholder="0"
                          onChange={e => {
                            const newValue = parseFloat(e.target.value) || 0
                            handleFieldChange(
                              'advancePayment',
                              newValue,
                              setFieldValue,
                              values
                            )
                          }}
                        />
                      </div>
                    )}
                  </Field>
                  <p className="text-xs text-gray-500 mt-1">
                    *Cantidad entre $0 y $10,000,000 MXN
                  </p>
                  <ErrorMessage
                    name="advancePayment"
                    component="div"
                    className="text-red-500 text-sm mt-1"
                  />
                </div>

                {/* Residual Value */}
                <div className="mb-4">
                  <label
                    htmlFor="residualValue"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Valor residual*
                  </label>
                  <Field name="residualValue">
                    {({ field }: any) => (
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">
                          $
                        </span>
                        <Input
                          {...field}
                          type="number"
                          min="0"
                          max="10000000"
                          className="pl-8 no-spinner"
                          placeholder="0"
                          onChange={e => {
                            const newValue = parseFloat(e.target.value) || 0
                            handleFieldChange(
                              'residualValue',
                              newValue,
                              setFieldValue,
                              values
                            )
                          }}
                        />
                      </div>
                    )}
                  </Field>
                  <p className="text-xs text-gray-500 mt-1">
                    *Cantidad entre $0 y $10,000,000 MXN
                  </p>
                  <ErrorMessage
                    name="residualValue"
                    component="div"
                    className="text-red-500 text-sm mt-1"
                  />
                </div>

                {/* Guarantee Deposit */}
                <div className="mb-4">
                  <label
                    htmlFor="guaranteeDeposit"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Depósito en garantía*
                  </label>
                  <Field name="guaranteeDeposit">
                    {({ field }: any) => (
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">
                          $
                        </span>
                        <Input
                          {...field}
                          type="number"
                          min="0"
                          max="500000"
                          className="pl-8 no-spinner"
                          placeholder="0"
                          onChange={e => {
                            const newValue = parseFloat(e.target.value) || 0
                            handleFieldChange(
                              'guaranteeDeposit',
                              newValue,
                              setFieldValue,
                              values
                            )
                          }}
                        />
                      </div>
                    )}
                  </Field>
                  <p className="text-xs text-gray-500 mt-1">
                    *Cantidad entre $0 y $500,000 MXN
                  </p>
                  <ErrorMessage
                    name="guaranteeDeposit"
                    component="div"
                    className="text-red-500 text-sm mt-1"
                  />
                </div>
              </div>

              {/* Hidden submit button for external triggering */}
              <button type="submit" style={{ display: 'none' }} />
            </Form>
          )
        }}
      </Formik>

      {/* Custom styles */}
      <style>{`
        .slider::-webkit-slider-thumb {
          appearance: none;
          height: 20px;
          width: 20px;
          border-radius: 50%;
          background: #10b981;
          cursor: pointer;
          border: 2px solid #ffffff;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
        }

        .slider::-moz-range-thumb {
          height: 20px;
          width: 20px;
          border-radius: 50%;
          background: #10b981;
          cursor: pointer;
          border: 2px solid #ffffff;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
        }

        /* Hide number input spinners */
        .no-spinner::-webkit-outer-spin-button,
        .no-spinner::-webkit-inner-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }

        .no-spinner[type=number] {
          -moz-appearance: textfield;
        }
      `}</style>
    </div>
  )
}

export default ProposalReviewForm
